const UTM_KEY = 'lh_utm'
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const

export interface StoredAttribution {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  referrer_domain?: string
}

/**
 * Capture UTMs from URL params into sessionStorage on page load.
 * If no UTMs present, falls back to storing document.referrer hostname.
 * Only writes on first landing — never overwrites existing data with empty.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return

  // Don't overwrite existing attribution data
  const existing = sessionStorage.getItem(UTM_KEY)
  if (existing) return

  const params = new URLSearchParams(window.location.search)
  const attribution: StoredAttribution = {}
  let hasUtm = false

  for (const key of UTM_PARAMS) {
    const value = params.get(key)
    if (value) {
      attribution[key] = value
      hasUtm = true
    }
  }

  // Fallback: capture referrer domain if no UTMs
  if (!hasUtm && document.referrer) {
    try {
      const referrerHost = new URL(document.referrer).hostname
      // Only store external referrers (not self-referrals)
      if (referrerHost && !referrerHost.includes('lifehack.org')) {
        attribution.referrer_domain = referrerHost
      }
    } catch {
      // Invalid referrer URL — skip
    }
  }

  // Only store if we captured something
  if (hasUtm || attribution.referrer_domain) {
    sessionStorage.setItem(UTM_KEY, JSON.stringify(attribution))
  }
}

/**
 * Retrieve stored attribution data from sessionStorage.
 */
export function getStoredAttribution(): StoredAttribution | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = sessionStorage.getItem(UTM_KEY)
    if (!raw) return null
    return JSON.parse(raw) as StoredAttribution
  } catch {
    return null
  }
}
